import { useLayoutEffect, useRef } from "react";

/**
 * Pixel-snap an `auto-fill` grid so its columns are INTEGER-width.
 *
 * A `repeat(auto-fill, minmax(min, 1fr))` grid divides the row into N tracks of
 * `available / N` px — usually fractional (e.g. 285.333px), which puts each
 * card's left edge on a sub-pixel offset (0, ⅓, ⅔…). Text then lands between
 * physical pixels and blurs / picks up an LCD colour fringe, inconsistently per
 * card and per viewport width. Pure CSS can't round `1fr` to integers.
 *
 * This hook measures the container, computes the same column count auto-fill
 * would, then sets explicit `repeat(N, floor(cardW)px)` so every track — and
 * therefore every card origin — is integer. Cards still stretch to fill (minus
 * a few px of rounding slack). Progressive enhancement: the CSS `1fr` rule is
 * the no-JS fallback; this just sharpens it.
 *
 * Companion to the grayscale-AA `filter` trick (which removes the colour
 * fringe): this removes the sub-pixel *position*, that removes the *fringe*.
 */
export function usePixelSnapGrid<T extends HTMLElement>(opts?: { min?: number; gap?: number }) {
	const { min = 260, gap = 16 } = opts ?? {};
	const ref = useRef<T>(null);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el || typeof ResizeObserver === "undefined") return;

		const apply = () => {
			const avail = el.clientWidth;
			if (avail <= 0) return;
			const cols = Math.max(1, Math.floor((avail + gap) / (min + gap)));
			const cardW = Math.floor((avail - (cols - 1) * gap) / cols);
			el.style.gridTemplateColumns = `repeat(${cols}, ${cardW}px)`;
		};

		// Defer the DOM write out of the observer callback via rAF, otherwise the
		// synchronous layout change re-triggers the observer -> "ResizeObserver
		// loop completed with undelivered notifications".
		let raf = 0;
		const ro = new ResizeObserver(() => {
			cancelAnimationFrame(raf);
			raf = requestAnimationFrame(apply);
		});
		ro.observe(el);
		apply();
		return () => {
			cancelAnimationFrame(raf);
			ro.disconnect();
		};
	}, [min, gap]);

	return ref;
}
